import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Package, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

const statusColors = {
  unfulfilled: { bg: 'rgba(239,68,68,0.1)', text: '#dc2626' },
  processing: { bg: 'rgba(234,179,8,0.1)', text: '#ca8a04' },
  shipped: { bg: 'rgba(59,130,246,0.1)', text: '#2563eb' },
  in_transit: { bg: 'rgba(99,102,241,0.1)', text: '#6366f1' },
  delivered: { bg: 'rgba(34,197,94,0.1)', text: '#16a34a' },
  refunded: { bg: 'rgba(107,114,128,0.1)', text: '#6b7280' },
};
const paymentColors = {
  paid: { bg: 'rgba(34,197,94,0.1)', text: '#16a34a' },
  refunded: { bg: 'rgba(107,114,128,0.1)', text: '#6b7280' },
  pending: { bg: 'rgba(234,179,8,0.1)', text: '#ca8a04' },
};

export default function OrderListPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, sort: '-created_at' });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await api.get(`/orders?${params}`);
      setOrders(data.orders);
      setTotal(data.total);
      setPages(data.pages);
    } catch {}
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <motion.div data-testid="order-list-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Orders</h1>
          <p className="font-sans text-sm mt-1" style={{ color: 'rgba(26,26,26,0.5)' }}>{total} orders</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(26,26,26,0.35)' }} />
          <Input data-testid="order-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search orders..." className="pl-9 font-sans text-sm rounded-none" style={{ borderColor: 'rgba(26,26,26,0.12)' }} />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger data-testid="order-status-filter" className="w-[160px] font-sans text-sm rounded-none" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="unfulfilled">Unfulfilled</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              {['Order', 'Date', 'Customer', 'Total', 'Payment', 'Fulfillment', ''].map(h => (
                <th key={h} className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium" style={{ color: 'rgba(26,26,26,0.45)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={7} className="p-12 text-center">
                <Package size={32} strokeWidth={1} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.2)' }} />
                <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No orders found</p>
              </td></tr>
            ) : orders.map(o => {
              const fc = statusColors[o.fulfillment_status] || statusColors.unfulfilled;
              const pc = paymentColors[o.payment_status] || paymentColors.pending;
              return (
                <tr key={o.id} className="border-t hover:bg-[rgba(26,26,26,0.01)] cursor-pointer transition-colors" style={{ borderColor: 'rgba(26,26,26,0.06)' }}
                  onClick={() => navigate(`/admin/orders/${o.id}`)}>
                  <td className="p-3">
                    <span data-testid={`order-num-${o.order_number}`} className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>#{o.order_number}</span>
                    {o.is_test && <span className="ml-1 text-[9px] px-1 py-0.5 uppercase rounded" style={{ backgroundColor: '#ca8a04', color: '#fff' }}>Test</span>}
                  </td>
                  <td className="p-3"><span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{new Date(o.created_at).toLocaleDateString()}</span></td>
                  <td className="p-3">
                    <p className="font-sans text-sm" style={{ color: '#1A1A1A' }}>{o.customer_name || '—'}</p>
                    <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.4)' }}>{o.customer_email}</p>
                  </td>
                  <td className="p-3"><span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>${o.total?.toFixed(2)}</span></td>
                  <td className="p-3">
                    <Badge className="rounded-sm font-sans text-[10px] uppercase tracking-wider" style={{ backgroundColor: pc.bg, color: pc.text, border: 'none' }}>{o.payment_status}</Badge>
                  </td>
                  <td className="p-3">
                    <Badge className="rounded-sm font-sans text-[10px] uppercase tracking-wider" style={{ backgroundColor: fc.bg, color: fc.text, border: 'none' }}>{o.fulfillment_status}</Badge>
                  </td>
                  <td className="p-3">
                    <Link to={`/admin/orders/${o.id}`} data-testid={`view-order-${o.id}`}
                      className="font-sans text-xs font-medium" style={{ color: '#C4956A' }}>View</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.5)' }}>Page {page} of {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="font-sans text-xs rounded-none">Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="font-sans text-xs rounded-none">Next</Button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
